import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import * as supplierActions from '../_actions/supplier';
import { Supplier } from '../_models';

export interface SupplierState extends EntityState<Supplier> {
  loading: boolean;
  error: any;
}

/**
 * Entity adapter for suppliers.
 * No sortComparer is used - API returns data sorted by dateCreated DESC.
 * New items are manually prepended in addSupplierSuccess reducer.
 */
export const adapter: EntityAdapter<Supplier> = createEntityAdapter<Supplier>();

const initialState: SupplierState = adapter.getInitialState({
  loading: false,
  error: null,
});

const supplierReducer = createReducer(
  initialState,

  on(supplierActions.loadSuppliers, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(supplierActions.loadSuppliersSuccess, (state, { suppliers }) =>
    adapter.setAll(suppliers, { ...state, loading: false })
  ),

  on(supplierActions.loadSuppliersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  /**
   * Manually prepend new supplier to the beginning of the list.
   * Direct state manipulation is used for performance (O(n) prepend vs O(n log n) sort).
   * Filter removes duplicate ID if present, then prepends new ID to maintain newest-first order.
   */
  on(supplierActions.addSupplierSuccess, (state, { supplier }) => ({
    ...state,
    entities: { ...state.entities, [supplier.id]: supplier },
    ids: [supplier.id, ...(state.ids as number[]).filter((id) => id !== supplier.id)],
  })),

  on(supplierActions.updateSupplierSuccess, (state, { supplier }) =>
    adapter.upsertOne(supplier, state)
  ),

  on(supplierActions.deleteSupplierSuccess, (state, { id }) => adapter.removeOne(id, state)),

  on(
    supplierActions.addSupplierFailure,
    supplierActions.updateSupplierFailure,
    supplierActions.deleteSupplierFailure,
    (state, { error }) => ({
      ...state,
      error,
    })
  )
);

export function reducer(state: SupplierState | undefined, action: Action) {
  return supplierReducer(state, action);
}

export const { selectAll, selectEntities, selectTotal } = adapter.getSelectors();
